import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FileQuestion, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { exportCsv, exportGlobalCsv } from '../../store/reporting/actions';
import { formatDateTimeForAPI } from '../../constants/utils';
import DateRangeSelector from './DateRangeSelector';
import { useTranslation } from 'react-i18next';

const ExportRequestsForm = ({ organizationId, mode = 'org' }) => {
  const isGlobal = mode === 'global';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { exportLoading } = useSelector(state => state.Reporting);
  const [format, setFormat] = useState('csv');

  // Local date range state
  const getDefaultDateRange = () => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    startDate.setHours(0, 0, 0, 0);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange({ startDate, endDate });
  };

  const handleExport = () => {
    const dateParams = {
      startDate: formatDateTimeForAPI(dateRange.startDate),
      endDate: formatDateTimeForAPI(dateRange.endDate),
    };
    if (isGlobal) {
      dispatch(exportGlobalCsv('service-requests', dateParams));
      return;
    }
    if (!organizationId) return;
    dispatch(exportCsv('service-requests', { organizationId, ...dateParams }));
  };

  return (
    <div className="p-4">
      <div className="d-flex align-items-center gap-2 mb-4">
        <FileQuestion size={20} className="text-primary" />
        <h5 className="mb-0 fw-bold">{t('reporting.export.requests_title', 'Export Request List')}</h5>
      </div>

      <p className="text-muted mb-4">
        {isGlobal
          ? t('reporting.export.requests_description_global', 'Export service requests across all your organizations within the selected date range. The first column identifies which organization each row belongs to.')
          : t('reporting.export.requests_description', 'Export service requests for your organization within the selected date range.')}
      </p>

      {/* Date Range */}
      <div className="mb-4">
        <label className="form-label fw-semibold">{t('shared.date_range', 'Date Range')}</label>
        <div>
          <DateRangeSelector
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
          />
        </div>
      </div>

      {/* Export Format */}
      <div className="mb-4">
        <label className="form-label fw-semibold">{t('shared.export_format', 'Export Format')}</label>
        <div className="d-flex gap-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="requestExportFormat"
              id="requestFormatCsv"
              value="csv"
              checked={format === 'csv'}
              onChange={(e) => setFormat(e.target.value)}
            />
            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="requestFormatCsv">
              <FileSpreadsheet size={16} />
              {t('shared.csv', 'CSV')}
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="requestExportFormat"
              id="requestFormatPdf"
              value="pdf"
              disabled
            />
            <label className="form-check-label d-flex align-items-center gap-1 text-muted" htmlFor="requestFormatPdf">
              <FileText size={16} />
              {t('shared.pdf', 'PDF')} <span className="badge bg-light text-muted ms-1">{t('shared.coming_soon', 'Coming soon')}</span>
            </label>
          </div>
        </div>
      </div>

      {/* Export Button */}
      <motion.button
        className="btn btn-primary d-flex align-items-center gap-2"
        onClick={handleExport}
        disabled={exportLoading || (!isGlobal && !organizationId)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {exportLoading ? (
          <>
            <span className="spinner-border spinner-border-sm" />
            {t('shared.exporting', 'Exporting...')}
          </>
        ) : (
          <>
            <Download size={18} />
            {t('reporting.export.button_export_requests', 'Export Requests')}
          </>
        )}
      </motion.button>
    </div>
  );
};

export default ExportRequestsForm;
